/**
 * Élément léger renvoyé par les endpoints `/lookup` et `/lookup/{id}`
 * d'Acts, Drugs et Diseases. Destiné aux selects / comboboxes — pour les
 * DTO complets, utiliser les endpoints paginés du catalogue ou
 * `getXxxById`.
 *
 * @export
 * @interface LookupItem
 */
export interface LookupItem {
  id: number;
  label: string;
}

/**
 * Réponse du module reporting financier
 * ({@code GET /financial-report?from&to&...}). Ajoutée 2026-05-26.
 *
 * <p>Le champ {@code invoices} est une page {@code PageMapper.toMap}
 * (même shape que {@code /invoice/by-patient/{id}?page&size} sauf que
 * {@code content} contient ici la projection serveur — alignée avec ce
 * que le back renvoie effectivement).
 */
export interface FinancialReportResponse {
  period: { from: string; to: string; groupBy?: string };
  filters: {
    statuses?: string[];
    practitionerId?: number | null;
    insurerId?: number | null;
  };
  summary: {
    invoiceCount: number;
    paidCount: number;
    pendingCount: number;
    failedCount: number;
    totalBrut: number;
    totalInsurerCoverage: number;
    totalInvoiceAmount: number;
    totalPaid: number;
    totalRemaining: number;
    collectionRate: number;
    averageInvoiceAmount: number;
  };
  byStatus: Array<{
    status: "PENDING" | "PAID" | "FAILED" | string;
    count: number;
    totalInvoiceAmount: number;
    totalPaid: number;
    totalRemaining: number;
  }>;
  byPractitioner: Array<{
    practitionerId: number;
    practitionerFirstName: string;
    practitionerLastName: string;
    invoiceCount: number;
    totalInvoiceAmount: number;
    totalPaid: number;
    totalRemaining: number;
  }>;
  byInsurer: Array<{
    insurerId: number;
    insurerName: string;
    invoiceCount: number;
    totalInsurerCoverage: number;
    totalPatientPart: number;
  }>;
  byActsType: Array<{
    actsTypeName: "CONSULTATION" | "EXAM" | string;
    lineCount: number;
    revenue: number;
  }>;
  topActs: Array<{
    actsId: number;
    actsLabel: string;
    actsTypeName: string;
    quantity: number;
    revenue: number;
  }>;
  paymentMethods: Array<{
    method: string;
    count: number;
    totalAmount: number;
  }>;
  timeseries: Array<{
    bucket: string;
    invoiceCount: number;
    totalInvoiceAmount: number;
    totalPaid: number;
    totalRemaining: number;
  }>;
  invoices: {
    content: any[];
    totalElements?: number;
    totalPages?: number;
    currentPage?: number;
    pageSize?: number;
    hasNext?: boolean;
    hasPrevious?: boolean;
  };
}

/**
 * Variante d'{@link LookupItem} renvoyée par `/acts/lookup` — étend le
 * lookup générique avec les deux champs strictement nécessaires aux
 * formulaires de facturation (TabThree « Bons ») :
 * - `price` : tarif catalogue (fallback quand le patient n'a pas
 *   d'assureur ou que l'acte n'est pas couvert).
 * - `actsTypeName` : permet de choisir `examPercent`
 *   vs `consultationPercent` côté front sans round-trip.
 *
 * @export
 * @interface ActsLookupItem
 */
export interface ActsLookupItem extends LookupItem {
  price?: number;
  actsTypeName?: string;
}

/**
 *
 * @export
 * @interface Acts
 */
export interface Acts {
  /**
   *
   * @type {string}
   * @memberof Acts
   */
  actsTypeName?: Acts.ActsTypeNameEnum;
  /**
   *
   * @type {number}
   * @memberof Acts
   */
  basePrice?: number;
  /**
   *
   * @type {string}
   * @memberof Acts
   */
  description?: string;
  /**
   *
   * @type {number}
   * @memberof Acts
   */
  id?: number;
  /**
   *
   * @type {string}
   * @memberof Acts
   */
  label?: string;
}

/**
 * @export
 * @namespace Acts
 */
export namespace Acts {
  /**
   * @export
   * @enum {string}
   */
  export enum ActsTypeNameEnum {
    CONSULTATION = <any>"CONSULTATION",
    EXAM = <any>"EXAM",
  }
}
/**
 *
 * @export
 * @interface ActsDiagnosticRequest
 */
export interface ActsDiagnosticRequest {
  /**
   *
   * @type {number}
   * @memberof ActsDiagnosticRequest
   */
  actsId?: number;
  /**
   *
   * @type {string}
   * @memberof ActsDiagnosticRequest
   */
  resultEyeLeft?: string;
  /**
   *
   * @type {string}
   * @memberof ActsDiagnosticRequest
   */
  resultEyeRight?: string;
  /**
   *
   * @type {string}
   * @memberof ActsDiagnosticRequest
   */
  resultFile?: string;
}
/**
 *
 * @export
 * @interface ActsPricePerInsurerRequest
 */
export interface ActsPricePerInsurerRequest {
  /**
   *
   * @type {number}
   * @memberof ActsPricePerInsurerRequest
   */
  actsId: number;
  /**
   *
   * @type {number}
   * @memberof ActsPricePerInsurerRequest
   */
  insurerId: number;
  /**
   *
   * @type {number}
   * @memberof ActsPricePerInsurerRequest
   */
  price: number;
}
/**
 *
 * @export
 * @interface AdminRequest
 */
export interface AdminRequest {
  /**
   *
   * @type {string}
   * @memberof AdminRequest
   */
  avatar?: string;
  /**
   *
   * @type {string}
   * @memberof AdminRequest
   */
  email: string;
  /**
   *
   * @type {string}
   * @memberof AdminRequest
   */
  firstName?: string;
  /**
   *
   * @type {string}
   * @memberof AdminRequest
   */
  lastName?: string;
  /**
   *
   * @type {string}
   * @memberof AdminRequest
   */
  password: string;
  /**
   *
   * @type {string}
   * @memberof AdminRequest
   */
  phoneNumber?: string;
  /**
   *
   * @type {string}
   * @memberof AdminRequest
   */
  roleName: AdminRequest.RoleNameEnum;
  /**
   *
   * @type {string}
   * @memberof AdminRequest
   */
  userName: string;
}

/**
 * @export
 * @namespace AdminRequest
 */
export namespace AdminRequest {
  /**
   * @export
   * @enum {string}
   */
  export enum RoleNameEnum {
    ADMIN = <any>"ROLE_ADMIN",
    DOCTOR = <any>"ROLE_DOCTOR",
    INSURER = <any>"ROLE_INSURER",
    PATIENT = <any>"ROLE_PATIENT",
    SECRETARY = <any>"ROLE_SECRETARY",
  }
}
/**
 *
 * @export
 * @interface ApiResponse
 */
export interface ApiResponse<T = undefined> {
  /**
   *
   * @type {any}
   * @memberof ApiResponse
   */
  data?: T;
  /**
   *
   * @type {string}
   * @memberof ApiResponse
   */
  errorCode?: string;
  /**
   *
   * @type {{ [key: string]: string; }}
   * @memberof ApiResponse
   */
  errors?: { [key: string]: string };
  /**
   *
   * @type {string}
   * @memberof ApiResponse
   */
  message?: string;
  /**
   *
   * @type {string}
   * @memberof ApiResponse
   */
  statusCode?: ApiResponse.StatusCodeEnum;
  /**
   *
   * @type {boolean}
   * @memberof ApiResponse
   */
  success?: boolean;
  /**
   *
   * @type {{ [key: string]: string; }}
   * @memberof ApiResponse
   */
  warnings?: { [key: string]: string };
}

/**
 * @export
 * @namespace ApiResponse
 */
export namespace ApiResponse {
  /**
   * @export
   * @enum {string}
   */
  export enum StatusCodeEnum {
    ACCEPTED = <any>"ACCEPTED",
    ALREADYREPORTED = <any>"ALREADY_REPORTED",
    BADGATEWAY = <any>"BAD_GATEWAY",
    BADREQUEST = <any>"BAD_REQUEST",
    BANDWIDTHLIMITEXCEEDED = <any>"BANDWIDTH_LIMIT_EXCEEDED",
    CHECKPOINT = <any>"CHECKPOINT",
    CONFLICT = <any>"CONFLICT",
    CONTINUE = <any>"CONTINUE",
    CREATED = <any>"CREATED",
    DESTINATIONLOCKED = <any>"DESTINATION_LOCKED",
    EXPECTATIONFAILED = <any>"EXPECTATION_FAILED",
    FAILEDDEPENDENCY = <any>"FAILED_DEPENDENCY",
    FORBIDDEN = <any>"FORBIDDEN",
    FOUND = <any>"FOUND",
    GATEWAYTIMEOUT = <any>"GATEWAY_TIMEOUT",
    GONE = <any>"GONE",
    HTTPVERSIONNOTSUPPORTED = <any>"HTTP_VERSION_NOT_SUPPORTED",
    IMUSED = <any>"IM_USED",
    INSUFFICIENTSPACEONRESOURCE = <any>"INSUFFICIENT_SPACE_ON_RESOURCE",
    INSUFFICIENTSTORAGE = <any>"INSUFFICIENT_STORAGE",
    INTERNALSERVERERROR = <any>"INTERNAL_SERVER_ERROR",
    IAMATEAPOT = <any>"I_AM_A_TEAPOT",
    LENGTHREQUIRED = <any>"LENGTH_REQUIRED",
    LOCKED = <any>"LOCKED",
    LOOPDETECTED = <any>"LOOP_DETECTED",
    METHODFAILURE = <any>"METHOD_FAILURE",
    METHODNOTALLOWED = <any>"METHOD_NOT_ALLOWED",
    MOVEDPERMANENTLY = <any>"MOVED_PERMANENTLY",
    MOVEDTEMPORARILY = <any>"MOVED_TEMPORARILY",
    MULTIPLECHOICES = <any>"MULTIPLE_CHOICES",
    MULTISTATUS = <any>"MULTI_STATUS",
    NETWORKAUTHENTICATIONREQUIRED = <any>"NETWORK_AUTHENTICATION_REQUIRED",
    NONAUTHORITATIVEINFORMATION = <any>"NON_AUTHORITATIVE_INFORMATION",
    NOTACCEPTABLE = <any>"NOT_ACCEPTABLE",
    NOTEXTENDED = <any>"NOT_EXTENDED",
    NOTFOUND = <any>"NOT_FOUND",
    NOTIMPLEMENTED = <any>"NOT_IMPLEMENTED",
    NOTMODIFIED = <any>"NOT_MODIFIED",
    NOCONTENT = <any>"NO_CONTENT",
    OK = <any>"OK",
    PARTIALCONTENT = <any>"PARTIAL_CONTENT",
    PAYLOADTOOLARGE = <any>"PAYLOAD_TOO_LARGE",
    PAYMENTREQUIRED = <any>"PAYMENT_REQUIRED",
    PERMANENTREDIRECT = <any>"PERMANENT_REDIRECT",
    PRECONDITIONFAILED = <any>"PRECONDITION_FAILED",
    PRECONDITIONREQUIRED = <any>"PRECONDITION_REQUIRED",
    PROCESSING = <any>"PROCESSING",
    PROXYAUTHENTICATIONREQUIRED = <any>"PROXY_AUTHENTICATION_REQUIRED",
    REQUESTEDRANGENOTSATISFIABLE = <any>"REQUESTED_RANGE_NOT_SATISFIABLE",
    REQUESTENTITYTOOLARGE = <any>"REQUEST_ENTITY_TOO_LARGE",
    REQUESTHEADERFIELDSTOOLARGE = <any>"REQUEST_HEADER_FIELDS_TOO_LARGE",
    REQUESTTIMEOUT = <any>"REQUEST_TIMEOUT",
    REQUESTURITOOLONG = <any>"REQUEST_URI_TOO_LONG",
    RESETCONTENT = <any>"RESET_CONTENT",
    SEEOTHER = <any>"SEE_OTHER",
    SERVICEUNAVAILABLE = <any>"SERVICE_UNAVAILABLE",
    SWITCHINGPROTOCOLS = <any>"SWITCHING_PROTOCOLS",
    TEMPORARYREDIRECT = <any>"TEMPORARY_REDIRECT",
    TOOEARLY = <any>"TOO_EARLY",
    TOOMANYREQUESTS = <any>"TOO_MANY_REQUESTS",
    UNAUTHORIZED = <any>"UNAUTHORIZED",
    UNAVAILABLEFORLEGALREASONS = <any>"UNAVAILABLE_FOR_LEGAL_REASONS",
    UNPROCESSABLEENTITY = <any>"UNPROCESSABLE_ENTITY",
    UNSUPPORTEDMEDIATYPE = <any>"UNSUPPORTED_MEDIA_TYPE",
    UPGRADEREQUIRED = <any>"UPGRADE_REQUIRED",
    URITOOLONG = <any>"URI_TOO_LONG",
    USEPROXY = <any>"USE_PROXY",
    VARIANTALSONEGOTIATES = <any>"VARIANT_ALSO_NEGOTIATES",
  }
}
/**
 *
 * @export
 * @interface AppointmentRequest
 */
export interface AppointmentRequest {
  /**
   *
   * @type {string}
   * @memberof AppointmentRequest
   */
  appointmentDate?: string;
  /**
   *
   * @type {string}
   * @memberof AppointmentRequest
   */
  appointmentObject?: string;
  /**
   *
   * @type {string}
   * @memberof AppointmentRequest
   */
  appointmentTime?: string;
  /**
   *
   * @type {string}
   * @memberof AppointmentRequest
   */
  description?: string;
  /**
   *
   * @type {PatientRequest}
   * @memberof AppointmentRequest
   */
  patient?: PatientRequest;
  /**
   *
   * @type {UserRequest}
   * @memberof AppointmentRequest
   */
  doctor?: UserRequest;
}
/**
 *
 * @export
 * @interface BITemporalRequest
 */
export interface BITemporalRequest {
  /**
   *
   * @type {string}
   * @memberof BITemporalRequest
   */
  branchLength?: string;
  /**
   *
   * @type {boolean}
   * @memberof BITemporalRequest
   */
  doubleHearths?: boolean;
  /**
   *
   * @type {string}
   * @memberof BITemporalRequest
   */
  interpupillaryDistance?: string;
  /**
   *
   * @type {boolean}
   * @memberof BITemporalRequest
   */
  progressive?: boolean;
  /**
   *
   * @type {boolean}
   * @memberof BITemporalRequest
   */
  shadePbx?: boolean;
  /**
   *
   * @type {boolean}
   * @memberof BITemporalRequest
   */
  shadePgx?: boolean;
  /**
   *
   * @type {boolean}
   * @memberof BITemporalRequest
   */
  shadeWhite?: boolean;
}
/**
 *
 * @export
 * @interface ConsultationRequest
 */
export interface ConsultationRequest {
  /**
   *
   * @type {Array<ActsDiagnosticRequest>}
   * @memberof ConsultationRequest
   */
  actsDiagnosticRequests: Array<ActsDiagnosticRequest>;
  /**
   *
   * @type {string}
   * @memberof ConsultationRequest
   */
  appointmentDate?: string;
  /**
   *
   * @type {string}
   * @memberof ConsultationRequest
   */
  appointmentObject?: string;
  /**
   *
   * @type {AppointmentRequest}
   * @memberof ConsultationRequest
   */
  appointmentRequest?: AppointmentRequest;
  /**
   *
   * @type {string}
   * @memberof ConsultationRequest
   */
  appointmentTime?: string;
  /**
   *
   * @type {BITemporalRequest}
   * @memberof ConsultationRequest
   */
  biTemporalRequest?: BITemporalRequest;
  /**
   *
   * @type {string}
   * @memberof ConsultationRequest
   */
  complaint: string;
  /**
   *
   * @type {string}
   * @memberof ConsultationRequest
   */
  description?: string;
  /**
   *
   * @type {string}
   * @memberof ConsultationRequest
   */
  diagnostic: string;
  /**
   *
   * @type {number}
   * @memberof ConsultationRequest
   */
  doctorId: number;
  /**
   *
   * @type {Array<DosageRequest>}
   * @memberof ConsultationRequest
   */
  dosageRequests?: Array<DosageRequest>;
  /**
   *
   * @type {number}
   * @memberof ConsultationRequest
   */
  patientId: number;
  /**
   *
   * @type {PrismRequest}
   * @memberof ConsultationRequest
   */
  prismRequest?: PrismRequest;
  /**
   *
   * @type {Array<VisionRequest>}
   * @memberof ConsultationRequest
   */
  visionRequests?: Array<VisionRequest>;
}
/**
 *
 * @export
 * @interface DiseasesRequest
 */
export interface DiseasesRequest {
  /**
   *
   * @type {string}
   * @memberof DiseasesRequest
   */
  description?: string;
  /**
   *
   * @type {string}
   * @memberof DiseasesRequest
   */
  name: string;
}
/**
 *
 * @export
 * @interface DosageRequest
 */
export interface DosageRequest {
  /**
   *
   * @type {number}
   * @memberof DosageRequest
   */
  drugsId: number;
  /**
   *
   * @type {string}
   * @memberof DosageRequest
   */
  drugsType?: DosageRequest.DrugsTypeEnum;
  /**
   *
   * @type {string}
   * @memberof DosageRequest
   */
  duration?: string;
  /**
   *
   * @type {string}
   * @memberof DosageRequest
   */
  frequency: string;
  /**
   *
   * @type {number}
   * @memberof DosageRequest
   */
  quantity: number;
  /**
   *
   * @type {string}
   * @memberof DosageRequest
   */
  timeOfDays?: string;
}

/**
 * @export
 * @namespace DosageRequest
 */
export namespace DosageRequest {
  /**
   * @export
   * @enum {string}
   */
  export enum DrugsTypeEnum {
      EYEDROPS = <any> 'EYE_DROPS',
      TABLETS = <any> 'TABLETS',
      SYRUP = <any> 'SYRUP',
      CREAM = <any> 'CREAM',
      CAPSULES = <any> 'CAPSULES',

  }
}
/**
 *
 * @export
 * @interface Drugs
 */
export interface Drugs {
  /**
   *
   * @type {number}
   * @memberof Drugs
   */
  basePrice?: number;
  /**
   *
   * @type {string}
   * @memberof Drugs
   */
  codeDrugs?: string;
  /**
   *
   * @type {number}
   * @memberof Drugs
   */
  id?: number;
  /**
   *
   * @type {string}
   * @memberof Drugs
   */
  name?: string;
  /**
   *
   * @type {number}
   * @memberof Drugs
   */
  stock?: number;
}
/**
 *
 * @export
 * @interface DrugsStockRequest
 */
export interface DrugsStockRequest {
  /**
   *
   * @type {number}
   * @memberof DrugsStockRequest
   */
  drugsId: number;
  /**
   *
   * @type {number}
   * @memberof DrugsStockRequest
   */
  quantity: number;
  /**
   *
   * @type {number}
   * @memberof DrugsStockRequest
   */
  userId: number;
}
/**
 *
 * @export
 * @interface EmployeeWorkScheduleRequest
 */
export interface EmployeeWorkScheduleRequest {
  /**
   *
   * @type {string}
   * @memberof EmployeeWorkScheduleRequest
   */
  dayOfWeek?: EmployeeWorkScheduleRequest.DayOfWeekEnum;
  /**
   *
   * @type {number}
   * @memberof EmployeeWorkScheduleRequest
   */
  id?: number;
  /**
   *
   * @type {string}
   * @memberof EmployeeWorkScheduleRequest
   */
  timeEnd?: string;
  /**
   *
   * @type {string}
   * @memberof EmployeeWorkScheduleRequest
   */
  timeStart?: string;
}

/**
 * @export
 * @namespace EmployeeWorkScheduleRequest
 */
export namespace EmployeeWorkScheduleRequest {
  /**
   * @export
   * @enum {string}
   */
  export enum DayOfWeekEnum {
    FRIDAY = <any>"FRIDAY",
    MONDAY = <any>"MONDAY",
    SATURDAY = <any>"SATURDAY",
    SUNDAY = <any>"SUNDAY",
    THURSDAY = <any>"THURSDAY",
    TUESDAY = <any>"TUESDAY",
    WEDNESDAY = <any>"WEDNESDAY",
  }
}
/**
 *
 * @export
 * @interface ExamRequestRequest
 */
export interface ExamRequestRequest {
  /**
   *
   * @type {Array<number>}
   * @memberof ExamRequestRequest
   */
  actsId: Array<number>;
  /**
   *
   * @type {number}
   * @memberof ExamRequestRequest
   */
  doctorId: number;
  /**
   *
   * @type {number}
   * @memberof ExamRequestRequest
   */
  patientId: number;
}
/**
 *
 * @export
 * @interface InputStream
 */
export interface InputStream {}
/**
 *
 * @export
 * @interface InputStreamResource
 */
export interface InputStreamResource {
  /**
   *
   * @type {string}
   * @memberof InputStreamResource
   */
  description?: string;
  /**
   *
   * @type {Blob}
   * @memberof InputStreamResource
   */
  file?: Blob;
  /**
   *
   * @type {string}
   * @memberof InputStreamResource
   */
  filename?: string;
  /**
   *
   * @type {InputStream}
   * @memberof InputStreamResource
   */
  inputStream?: InputStream;
  /**
   *
   * @type {boolean}
   * @memberof InputStreamResource
   */
  open?: boolean;
  /**
   *
   * @type {boolean}
   * @memberof InputStreamResource
   */
  readable?: boolean;
  /**
   *
   * @type {string}
   * @memberof InputStreamResource
   */
  uri?: string;
  /**
   *
   * @type {string}
   * @memberof InputStreamResource
   */
  url?: string;
}
/**
 *
 * @export
 * @interface InsurerRequest
 */
export interface InsurerRequest {
  /**
   *
   * @type {string}
   * @memberof InsurerRequest
   */
  address: string;
  /**
   *
   * @type {string}
   * @memberof InsurerRequest
   */
  email?: string;
  /**
   *
   * @type {string}
   * @memberof InsurerRequest
   */
  name: string;
  /**
   *
   * @type {string}
   * @memberof InsurerRequest
   */
  phoneNumber: string;
}
/**
 *
 * @export
 * @interface InvoiceActsRequest
 */
export interface InvoiceActsRequest {
  /**
   *
   * @type {number}
   * @memberof InvoiceActsRequest
   */
  actsId: number;
  /**
   *
   * @type {number}
   * @memberof InvoiceActsRequest
   */
  amountPaid: number;
  /**
   *
   * @type {number}
   * @memberof InvoiceActsRequest
   */
  insurerCoverage: number;
  /**
   *
   * @type {number}
   * @memberof InvoiceActsRequest
   */
  invoiceAmount: number;
  /**
   *
   * @type {number}
   * @memberof InvoiceActsRequest
   */
  price: number;
  /**
   *
   * @type {number}
   * @memberof InvoiceActsRequest
   */
  quantity: number;
  /**
   *
   * @type {number}
   * @memberof InvoiceActsRequest
   */
  remainingPaid: number;
  /**
   *
   * @type {number}
   * @memberof InvoiceActsRequest
   */
  totalInvoice: number;
   /**
   *
   * @type {Date}
   * @memberof InvoiceActsRequest
   */
   createdAt?: Date;
}
/**
 *
 * @export
 * @interface InvoiceRequest
 */
export interface InvoiceRequest {
  /**
   *
   * @type {Array<InvoiceActsRequest>}
   * @memberof InvoiceRequest
   */
  invoiceActsRequests?: Array<InvoiceActsRequest>;
  /**
   *
   * @type {Array<invoiceActsList>}
   * @memberof InvoiceRequest
   */
  invoiceActsList?: invoiceActsList[]
  /**
   *
   * @type {number}
   * @memberof InvoiceRequest
   */
  patientId: number;
  /**
   *
   * @type {number}
   * @memberof InvoiceRequest
   */
  practitionerId: number;
     /**
   *
   * @type {Date}
   * @memberof InvoiceRequest
   */
    createdAt?: Date;
  /**
   *
   * @type {string}
   * @memberof InvoiceRequest
   */
  paymentStatus?: string;
  /**
   *
   * @type {string}
   * @memberof InvoiceRequest
   */
  fedapayTransactionId?: string;
  /**
   *
   * @type {number}
   * @memberof InvoiceRequest
   */
  id?: number;
  /**
   *
   * @type {string}
   * @memberof InvoiceRequest
   */
  invoiceNumber?: string;
}
export interface invoiceActsList {
  /**
   *
   * @type {number}
   * @memberof invoiceActsList
   */
  id?: number;
   /**
   *
   * @type {number}
   * @memberof invoiceActsList
   */
   price?: number;
    /**
   *
   * @type {number}
   * @memberof invoiceActsList
   */
  totalInvoice?: number;
   /**
   *
   * @type {number}
   * @memberof invoiceActsList
   */
   insurerCoverage?: number;
    /**
   *
   * @type {number}
   * @memberof invoiceActsList
   */
    invoiceAmount?: number;
     /**
   *
   * @type {number}
   * @memberof invoiceActsList
   */
   amountPaid?: number;
    /**
   *
   * @type {number}
   * @memberof invoiceActsList
   */
    remainingPaid?: number;
   /**
   *
   * @type {Acts}
   * @memberof invoiceActsList
   */
   Acts?: Acts;
}

/**
 *
 * @export
 * @interface LoginRequest
 */
export interface LoginRequest {
  /**
   *
   * @type {string}
   * @memberof LoginRequest
   */
  password: string;
  /**
   * Identifiant de connexion : userName ou email.
   * @type {string}
   * @memberof LoginRequest
   */
  username: string;
}
/**
 *
 * @export
 * @interface MedicalFamilyHistoryRequest
 */
export interface MedicalFamilyHistoryRequest {
  /**
   *
   * @type {number}
   * @memberof MedicalFamilyHistoryRequest
   */
  diseasesId: number;
  /**
   *
   * @type {string}
   * @memberof MedicalFamilyHistoryRequest
   */
  historyTypeName: MedicalFamilyHistoryRequest.HistoryTypeNameEnum;
  /**
   *
   * @type {number}
   * @memberof MedicalFamilyHistoryRequest
   */
  id?: number;
}

/**
 * @export
 * @namespace MedicalFamilyHistoryRequest
 */
export namespace MedicalFamilyHistoryRequest {
  /**
   * @export
   * @enum {string}
   */
  export enum HistoryTypeNameEnum {
    FAMILYHISTORY = <any>"FAMILY_HISTORY",
    MEDICALHISTORY = <any>"MEDICAL_HISTORY",
  }
}
/**
 *
 * @export
 * @interface PatientRequest
 */
export interface PatientRequest {
  /**
   *
   * @type {string}
   * @memberof PatientRequest
   */
  address: string;
  /**
   * Nom de la personne à contacter en cas d'urgence (facultatif).
   * @type {string}
   * @memberof PatientRequest
   */
  emergencyContactName?: string;
  /**
   * Téléphone de la personne d'urgence (facultatif, format ^01[4569]\d{7}$).
   * @type {string}
   * @memberof PatientRequest
   */
  emergencyContactPhone?: string;
  /**
   * Numéro NPI (Numéro Personnel d'Identification) du patient (facultatif).
   * @type {string}
   * @memberof PatientRequest
   */
  npiNumber?: string;
  /**
   * Date de naissance au format ISO {@code yyyy-MM-dd}. Facultative, jamais
   * dans le futur. En requête elle prime sur {@code age} ; en réponse elle
   * vaut {@code null} tant qu'aucune date n'est connue.
   * @type {string}
   * @memberof PatientRequest
   */
  dateOfBirth?: string;
  /**
   * Champ de <em>réponse</em> uniquement (même double usage que {@code role}
   * sur {@code UserRequest}) : {@code true} quand la date de naissance a été
   * estimée à partir d'un âge (1ᵉʳ janvier) et reste donc à confirmer.
   * @type {boolean}
   * @memberof PatientRequest
   */
  dateOfBirthEstimated?: boolean;
  /**
   * En requête : âge approximatif (0–120) quand la date de naissance est
   * inconnue — le serveur en déduit une date estimée. En réponse : âge
   * calculé à partir de la date de naissance.
   * @type {number}
   * @memberof PatientRequest
   */
  age?: number;
  /**
   *
   * @type {string}
   * @memberof PatientRequest
   */
  consent?: string;
  /**
   *
   * @type {string}
   * @memberof PatientRequest
   */
  consultationPercent?: string;
  /**
   *
   * @type {number}
   * @memberof PatientRequest
   */
  doctorId: number;
  /**
   *
   * @type {string}
   * @memberof PatientRequest
   */
  email?: string;
  /**
   *
   * @type {string}
   * @memberof PatientRequest
   */
  examPercent?: string;
  /**
   *
   * @type {string}
   * @memberof PatientRequest
   */
  firstName: string;
  /**
   *
   * @type {number}
   * @memberof PatientRequest
   */
  insurerId?: number;
  /**
   *
   * @type {InsurerRequest}
   * @memberof PatientRequest
   */
  insurer?: InsurerRequest;
  /**
   *
   * @type {string}
   * @memberof PatientRequest
   */
  lastName: string;
  /**
   *
   * @type {string}
   * @memberof PatientRequest
   */
  occupation: string;
  /**
   *
   * @type {string}
   * @memberof PatientRequest
   */
  phoneNumber: string;
  /**
   *
   * @type {string}
   * @memberof PatientRequest
   */
  sex: string;
  /**
   *
   * @type {AdminRequest.RoleNameEnum}
   * @memberof PatientRequest
   */
  roleName?: AdminRequest.RoleNameEnum;
  /**
   *
   * @type {Date}
   * @memberof PatientRequest
   */
  createdAt?: Date;
}
/**
 *
 * @export
 * @interface PrismRequest
 */
export interface PrismRequest {
  /**
   *
   * @type {boolean}
   * @memberof PrismRequest
   */
  mineral?: boolean;
  /**
   *
   * @type {boolean}
   * @memberof PrismRequest
   */
  organic?: boolean;
  /**
   *
   * @type {boolean}
   * @memberof PrismRequest
   */
  transition?: boolean;
  /**
   *
   * @type {boolean}
   * @memberof PrismRequest
   */
  transitionAntiBlueLight?: boolean;
  /**
   *
   * @type {boolean}
   * @memberof PrismRequest
   */
  transitionAr?: boolean;
}
/**
 *
 * @export
 * @interface UpdateUserPasswordRequest
 */
export interface UpdateUserPasswordRequest {
  /**
   *
   * @type {string}
   * @memberof UpdateUserPasswordRequest
   */
  confirmPassword: string;
  /**
   *
   * @type {string}
   * @memberof UpdateUserPasswordRequest
   */
  newPassword?: string;
  /**
   *
   * @type {string}
   * @memberof UpdateUserPasswordRequest
   */
  token: string;
}
/**
 *
 * @export
 * @interface UserRequest
 */
export interface UserRequest {
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  address?: string;
  /**
   * Nom de la personne à contacter en cas d'urgence (facultatif).
   * @type {string}
   * @memberof UserRequest
   */
  emergencyContactName?: string;
  /**
   * Téléphone de la personne d'urgence (facultatif, format ^01[4569]\d{7}$).
   * @type {string}
   * @memberof UserRequest
   */
  emergencyContactPhone?: string;
  /**
   * Numéro NPI (Numéro Personnel d'Identification) du patient (facultatif).
   * @type {string}
   * @memberof UserRequest
   */
  npiNumber?: string;
  /**
   * Date de naissance au format ISO {@code yyyy-MM-dd}. Facultative, jamais
   * dans le futur. En requête elle prime sur {@code age} ; en réponse elle
   * vaut {@code null} tant qu'aucune date n'est connue.
   * @type {string}
   * @memberof UserRequest
   */
  dateOfBirth?: string;
  /**
   * Champ de <em>réponse</em> uniquement (même double usage que {@code role}
   * ci-dessous) : {@code true} quand la date de naissance a été estimée à
   * partir d'un âge (1ᵉʳ janvier) et reste donc à confirmer.
   * @type {boolean}
   * @memberof UserRequest
   */
  dateOfBirthEstimated?: boolean;
  /**
   * En requête : âge approximatif (0–120) quand la date de naissance est
   * inconnue — le serveur en déduit une date estimée. En réponse : âge
   * calculé à partir de la date de naissance.
   * @type {number}
   * @memberof UserRequest
   */
  age?: number;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  avatar?: string;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  consent?: string;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  consultationPercent?: string;
  /**
   *
   * @type {number}
   * @memberof UserRequest
   */
  doctorId?: number;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  email?: string;
  /**
   *
   * @type {Array<EmployeeWorkScheduleRequest>}
   * @memberof UserRequest
   */
  employeeWorkScheduleRequests?: Array<EmployeeWorkScheduleRequest>;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  examPercent?: string;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  firstName?: string;
  /**
   *
   * @type {number}
   * @memberof UserRequest
   */
  insurerId?: number;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  lastName?: string;
  /**
   *
   * @type {Array<MedicalFamilyHistoryRequest>}
   * @memberof UserRequest
   */
  medicalFamilyHistoryRequests?: Array<MedicalFamilyHistoryRequest>;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  name?: string;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  occupation?: string;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  otherInformation?: string;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  password?: string;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  phoneNumber?: string;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  sex?: string;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  userName?: string;
  /**
   *
   * @type {AdminRequest.RoleNameEnum}
   * @memberof UserRequest
   */
  roleName?: AdminRequest.RoleNameEnum;
  /**
   * Rôles additionnels cumulés au rôle principal. Champ à double usage,
   * comme {@code role} ci-dessous : en <em>requête</em> il porte les rôles
   * à attribuer (POST {@code /users/staff}, PUT {@code /users/{id}} ;
   * absent = inchangé), en <em>réponse</em> il reflète les rôles
   * effectivement détenus (jamais {@code null}, vide par défaut). Seuls des
   * rôles staff sont acceptés, et seulement sur un compte staff.
   * @type {Array<AdminRequest.RoleNameEnum>}
   * @memberof UserRequest
   */
  additionalRoles?: Array<AdminRequest.RoleNameEnum>;
  /**
   * Discriminant {@code role} de {@code UserResponse} côté back
   * (cf. {@code @JsonTypeInfo(property = "role")}). Présent ici parce que
   * {@code UserRequest} est aussi utilisé — abusivement — comme type de
   * réponse par {@code useGetUsers}. À terme : créer un vrai type
   * {@code UserResponse} polymorphe et migrer les composants.
   * @type {AdminRequest.RoleNameEnum}
   * @memberof UserRequest
   */
  role?: AdminRequest.RoleNameEnum;
  /**
   *
   * @type {Date}
   * @memberof UserRequest
   */
  createdAt?: Date;
  /**
   *
   * @type {string}
   * @memberof UserRequest
   */
  doctorQualifications?: string;
}
/**
 *
 * @export
 * @interface VisionRequest
 */
export interface VisionRequest {
  /**
   *
   * @type {string}
   * @memberof VisionRequest
   */
  axis?: string;
  /**
   *
   * @type {string}
   * @memberof VisionRequest
   */
  cylinder?: string;
  /**
   *
   * @type {string}
   * @memberof VisionRequest
   */
  eyeTypeName?: VisionRequest.EyeTypeNameEnum;
  /**
   *
   * @type {string}
   * @memberof VisionRequest
   */
  sphere?: string;
  /**
   *
   * @type {string}
   * @memberof VisionRequest
   */
  visionType?: VisionRequest.VisionTypeEnum;
}

/**
 * @export
 * @namespace VisionRequest
 */
export namespace VisionRequest {
  /**
   * @export
   * @enum {string}
   */
  export enum EyeTypeNameEnum {
    LEFT = <any>"EYE_LEFT",
    RIGHT = <any>"EYE_RIGHT",
  }
  /**
   * @export
   * @enum {string}
   */
  export enum VisionTypeEnum {
    AllDistanceVision = <any>"All_distance_vision",
    DistanceVision = <any>"Distance_vision",
    NearVision = <any>"Near_vision",
  }
}
